import React from "react";
import {
  Box,
  Button,
  IconButton,
  Modal,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "@hooks";
import { RootState } from "@Store";
import {
  createAppRequest,
  resetCreateAppModal,
} from "@Store/Apps/actions";
import {
  selectCreateAppModalLoading,
  selectShowCreateAppModal,
} from "@Store/Apps/selectors";

const CreateAppModal: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const { modalIsVisible, isLoading } = useAppSelector((state: RootState) => ({
    modalIsVisible: selectShowCreateAppModal(state),
    isLoading: selectCreateAppModalLoading(state),
  }));

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      appId: "",
      appName: "",
    },
    validationSchema: Yup.object({
      appId: Yup.string().required(t("errors.validation.requiredField")),
      appName: Yup.string().required(t("errors.validation.requiredField")),
    }),
    onSubmit: (values) => {
      dispatch(
        createAppRequest({
          appId: values.appId.trim(),
          appName: values.appName.trim(),
        })
      );
    },
  });

  React.useEffect(() => {
    if (!modalIsVisible) {
      formik.resetForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalIsVisible]);

  const handleClose = () => {
    if (isLoading) return;
    formik.resetForm();
    dispatch(resetCreateAppModal());
  };

  return (
    <Modal
      open={modalIsVisible}
      onClose={handleClose}
      aria-labelledby="create-app-title"
    >
      <Box
        className="modal-box"
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          bgcolor: "#232323",
          borderRadius: "8px",
          boxShadow: 24,
          width: "440px",
          color: "white",
          p: 4,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: "1px solid #363636",
            padding: "5px 10px",
            marginBottom: "16px",
          }}
        >
          <Typography id="create-app-title" variant="h6" component="h2">
            {t("apps.modals.addApp.title")}
          </Typography>
          <IconButton
            onClick={handleClose}
            sx={{ color: "white" }}
            disabled={isLoading}
          >
            <CloseIcon />
          </IconButton>
        </div>

        <form onSubmit={formik.handleSubmit}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {t("apps.modals.addApp.appId")}
          </Typography>
          <TextField
            fullWidth
            variant="outlined"
            name="appId"
            value={formik.values.appId}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.appId && Boolean(formik.errors.appId)}
            helperText={formik.touched.appId && formik.errors.appId}
            sx={{
              "& .MuiInputBase-input": { color: "white" },
              "& .MuiOutlinedInput-root": {
                "& fieldset": { borderColor: "#4C4C4C" },
              },
              mb: 2,
            }}
          />

          <Typography variant="body2" sx={{ mb: 1 }}>
            {t("apps.modals.addApp.appName")}
          </Typography>
          <TextField
            fullWidth
            variant="outlined"
            name="appName"
            value={formik.values.appName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.appName && Boolean(formik.errors.appName)}
            helperText={formik.touched.appName && formik.errors.appName}
            sx={{
              "& .MuiInputBase-input": { color: "white" },
              "& .MuiOutlinedInput-root": {
                "& fieldset": { borderColor: "#4C4C4C" },
              },
              mb: 2,
            }}
          />

          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
            <Button
              type="submit"
              variant="contained"
              color="warning"
              disabled={isLoading}
            >
              {t("apps.modals.addApp.submit")}
            </Button>
          </Box>
        </form>
      </Box>
    </Modal>
  );
};

export default CreateAppModal;
